/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { PostSigninInteractor } from "../../model/interactor";
import { SmartSearchScreenInteractor } from "../../model/interactor/shared";
import { AnalyticsLogger, Log, OperationalLogger } from "../../model/logging";
import { Locale } from "../../model/locale/Locale";
import { LocaleKeys } from "../../model/locale/LocaleKeys";
import type { TCarouselRail } from "../../model/types";
import { selectPreviousUrl } from "../screens";
import { getImageHostBaseUrl } from "../utility/smartCarouselUtility";
import {
  countCarouselItems,
  extractScreenPageCursor,
  extractSearchQueryCategory,
  mapSmartSearchScreenResponseToCarousels,
  normalizeSearchQuery
} from "./searchMappers";
import {
  SEARCH_MIN_QUERY_LENGTH,
  SEARCH_API_TIMEOUT_MS,
  SEARCH_MAX_RECENT_SEARCHES,
  SEARCH_RETRY_DELAY_MS,
  SEARCH_RESULTS_ANALYTICS_ID,
  SEARCH_RESULTS_ANALYTICS_NAME,
  SEARCH_SCREEN_ANALYTICS_ID,
  SEARCH_SCREEN_ANALYTICS_NAME
} from "./searchConstants";
import type {
  SearchFocusedField,
  SearchHistoryStatus,
  SearchState
} from "./search.types";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";

const TAG: string = "searchSlice";
const UNKNOWN_ERROR_MESSAGE: string = "Unknown error";
const SEARCH_ANALYTICS_LABEL: string = "search";

const SearchAnalyticsLogger = AnalyticsLogger.create({
  screenId: SEARCH_RESULTS_ANALYTICS_ID,
  screenName: SEARCH_RESULTS_ANALYTICS_NAME
});

const INITIAL_STATE: SearchState = {
  query: "",
  pendingSearchQuery: null,
  status: "idle",
  errorMessage: "",
  carousels: [],
  resolvedCount: 0,
  currentRequestId: null,
  pageCursor: null,
  isLoadingMore: false,
  resolvedQuery: "",
  recentSearches: [],
  historyStatus: "idle",
  historyRequestId: null,
  focusedField: "input",
  focusedHistoryIndex: 0,
  focusedCarouselIndex: 0,
  focusedTileIndices: []
};

const delay = (ms: number): Promise<void> =>
  new Promise((resolve): void => {
    setTimeout(resolve, ms);
  });

/**
 * Runs a smart-search request for `query`, mapping the interactor's
 * pre-grouped carousels response into TCarouselRails. Applies a client-side
 * timeout (SEARCH_API_TIMEOUT_MS) independent of the caller's cancellation
 * signal, and retries exactly once after SEARCH_RETRY_DELAY_MS on a
 * non-cancelled failure (PRD §3.3.2-3). `signal` is caller-owned — the
 * debounce/keystroke hook aborts the previous in-flight request's signal
 * before dispatching a new one (PRD §2.6.2.6).
 */
export const runSmartSearch = createAsyncThunk<
  {
    carousels: TCarouselRail[];
    resolvedCount: number;
    pageCursor: string | null;
    resolvedQuery: string;
  },
  { query: string; signal: AbortSignal },
  { rejectValue: { message: string; aborted: boolean }; state: UnityRootState }
>("search/runSmartSearch", async ({ query, signal }, thunkAPI) => {
  const { rejectWithValue, getState } = thunkAPI;
  const normalizedQuery: string = normalizeSearchQuery(query);
  const interactor: SmartSearchScreenInteractor =
    new SmartSearchScreenInteractor();
  const imageHostBaseUrl: string = await getImageHostBaseUrl(
    new PostSigninInteractor()
  );

  const isShortQuery: boolean =
    normalizedQuery.length < SEARCH_MIN_QUERY_LENGTH;
  let queryCategoryForEvent: string = SEARCH_ANALYTICS_LABEL;
  let requestSignal: AbortSignal = signal;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let onCallerAbort: (() => void) | null = null;

  if (!isShortQuery) {
    const timeoutController: AbortController = new AbortController();
    requestSignal = timeoutController.signal;
    onCallerAbort = (): void => timeoutController.abort();
    signal.addEventListener("abort", onCallerAbort);
    timeoutId = setTimeout((): void => {
      timeoutController.abort();
    }, SEARCH_API_TIMEOUT_MS);
  }

  try {
    let carousels: TCarouselRail[];
    let pageCursor: string | null;
    try {
      const response = await interactor.fetchSmartSearchScreen(
        { searchTerm: normalizedQuery, count: 4 },
        { signal: requestSignal }
      );
      carousels = mapSmartSearchScreenResponseToCarousels(
        response,
        imageHostBaseUrl
      );
      pageCursor = extractScreenPageCursor(response);
    } catch (firstError) {
      if (signal.aborted) {
        throw firstError;
      }
      Log.w(
        TAG,
        "runSmartSearch() first attempt failed, retrying once",
        firstError
      );
      await delay(SEARCH_RETRY_DELAY_MS);
      const response = await interactor.fetchSmartSearchScreen(
        { searchTerm: normalizedQuery, count: 4 },
        { signal: requestSignal }
      );
      carousels = mapSmartSearchScreenResponseToCarousels(
        response,
        imageHostBaseUrl
      );
      pageCursor = extractScreenPageCursor(response);
    }
    queryCategoryForEvent = extractSearchQueryCategory(carousels);
    const resolvedCount: number = countCarouselItems(carousels);
    if (resolvedCount === 0) {
      const noResultsMessage: string = Locale.getString(
        LocaleKeys.SEARCH_NO_RESULTS_MESSAGE
      ).replace("{{query}}", normalizedQuery);
      OperationalLogger.logUiError({
        screen: SEARCH_RESULTS_ANALYTICS_NAME,
        screenId: SEARCH_RESULTS_ANALYTICS_ID,
        errorMessage: noResultsMessage
      });
    }

    return {
      carousels,
      resolvedCount,
      pageCursor,
      resolvedQuery: normalizedQuery
    };
  } catch (error) {
    if (signal.aborted) {
      return rejectWithValue({ message: "aborted", aborted: true });
    }
    const errorMessage: string = Locale.getString(
      LocaleKeys.SEARCH_ERROR_MESSAGE
    );
    Log.e(TAG, "runSmartSearch() failed after retry", error);
    OperationalLogger.logError({
      errorMessage
    });
    OperationalLogger.logUiError({
      screen: SEARCH_RESULTS_ANALYTICS_NAME,
      screenId: SEARCH_RESULTS_ANALYTICS_ID,
      errorMessage
    });
    return rejectWithValue({ message: UNKNOWN_ERROR_MESSAGE, aborted: false });
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    if (onCallerAbort !== null) {
      signal.removeEventListener("abort", onCallerAbort);
    }
    // Fire once per submission (covers explicit + debounced auto-search).
    AnalyticsLogger.logSearchQuery({
      q: normalizedQuery,
      qc: queryCategoryForEvent,
      qsrc: SEARCH_ANALYTICS_LABEL,
      scid: SEARCH_SCREEN_ANALYTICS_ID,
      scr: SEARCH_SCREEN_ANALYTICS_NAME,
      pscid: selectPreviousUrl(getState())
    });
    Log.d(TAG, "runSmartSearch() - searchQuery event fired");
  }
});

/**
 * Loads the next page of result carousels using the server-issued pageCursor
 * (screen-level vertical pagination — more carousels, not more tiles). Reads
 * the current query and cursor from state; resolves with the newly-mapped
 * rails and the next cursor to append. Best-effort: no client timeout/retry
 * (unlike the foreground runSmartSearch), mirroring the homescreen
 * loadMoreCarousels prefetch. Skips when no cursor is available.
 */
export const loadMoreSmartSearchCarousels = createAsyncThunk<
  { carousels: TCarouselRail[]; pageCursor: string | null },
  void,
  { state: UnityRootState; rejectValue: { message: string } }
>(
  "search/loadMoreSmartSearchCarousels",
  async (_arg, { getState, rejectWithValue }) => {
    const { query, pageCursor } = getState().search;
    if (pageCursor === null || pageCursor.length === 0) {
      return { carousels: [], pageCursor: null };
    }
    try {
      const interactor: SmartSearchScreenInteractor =
        new SmartSearchScreenInteractor();
      const imageHostBaseUrl: string = await getImageHostBaseUrl(
        new PostSigninInteractor()
      );
      const response = await interactor.fetchSmartSearchScreen({
        searchTerm: normalizeSearchQuery(query),
        count: 4,
        pageCursor
      });
      return {
        carousels: mapSmartSearchScreenResponseToCarousels(
          response,
          imageHostBaseUrl
        ),
        pageCursor: extractScreenPageCursor(response)
      };
    } catch (error) {
      Log.e(TAG, "loadMoreSmartSearchCarousels() failed", error);
      OperationalLogger.logError({
        errorMessage: OperationalLogger.describeErrorMessage(error)
      });
      return rejectWithValue({ message: UNKNOWN_ERROR_MESSAGE });
    }
  }
);

/** Fetches the Recently Searched list on Search screen entry (PRD §2.4). */
export const fetchSearchHistoryThunk = createAsyncThunk<
  string[],
  void,
  { rejectValue: { message: string } }
>("search/fetchSearchHistory", async (_arg, { rejectWithValue }) => {
  try {
    const interactor: SmartSearchScreenInteractor =
      new SmartSearchScreenInteractor();
    return await interactor.fetchSearchHistory({});
  } catch (error) {
    Log.e(TAG, "fetchSearchHistoryThunk() failed", error);
    OperationalLogger.logError({
      errorMessage: OperationalLogger.describeErrorMessage(error)
    });
    return rejectWithValue({ message: UNKNOWN_ERROR_MESSAGE });
  }
});

/**
 * Persists the current in-memory Recently Searched list to TiVo Service.
 * Fired once on Search screen exit, per the API doc's "once exit from
 * search screen make API call to update search history" guidance —
 * not per-keystroke.
 */
export const saveSearchHistoryThunk = createAsyncThunk<
  void,
  string[],
  { rejectValue: { message: string } }
>("search/saveSearchHistory", async (searchTerms, { rejectWithValue }) => {
  if (searchTerms.length === 0) {
    return;
  }
  try {
    const interactor: SmartSearchScreenInteractor =
      new SmartSearchScreenInteractor();
    await interactor.createSearchHistory({ searchTerms });
  } catch (error) {
    Log.e(TAG, "saveSearchHistoryThunk() failed", error);
    OperationalLogger.logError({
      errorMessage: OperationalLogger.describeErrorMessage(error)
    });
    return rejectWithValue({ message: UNKNOWN_ERROR_MESSAGE });
  }
});

/**
 * Clears Recently Searched history via TiVo Service. The reducer clears
 * the local list optimistically on `pending` (PRD §2.4.6-7 — clearing is
 * expected to take effect immediately and propagate cross-device).
 */
export const deleteSearchHistoryThunk = createAsyncThunk<
  void,
  void,
  { rejectValue: { message: string } }
>("search/deleteSearchHistory", async (_arg, { rejectWithValue }) => {
  try {
    const interactor: SmartSearchScreenInteractor =
      new SmartSearchScreenInteractor();
    await interactor.deleteSearchHistory({});
  } catch (error) {
    Log.e(TAG, "deleteSearchHistoryThunk() failed", error);
    OperationalLogger.logError({
      errorMessage: OperationalLogger.describeErrorMessage(error)
    });
    return rejectWithValue({ message: UNKNOWN_ERROR_MESSAGE });
  }
});

/** Adds a term to the front of Recently Searched, deduped, capped at 6. */
const addRecentSearchTerm = (state: SearchState, term: string): void => {
  if (term.length === 0) {
    return;
  }
  const deduped: string[] = state.recentSearches.filter(
    (existing: string): boolean => existing !== term
  );
  state.recentSearches = [term, ...deduped].slice(
    0,
    SEARCH_MAX_RECENT_SEARCHES
  );
  // This list is now newer than anything a still-in-flight screen-entry fetch
  // could return (that request was issued before the term existed), and the
  // caller persists it — so drop the fetch's claim on this state.
  state.historyRequestId = null;
};

const searchSlice = createSlice({
  name: "search",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Sets the as-typed query. Reverting below SEARCH_MIN_QUERY_LENGTH
     * immediately clears any results/error state back to idle
     * (PRD §2.6.2.4) — callers are responsible for debouncing the
     * corresponding runSmartSearch dispatch.
     */
    setQuery(state, action: PayloadAction<string>): void {
      state.query = action.payload;
      const normalizedQuery: string = normalizeSearchQuery(action.payload);
      state.pendingSearchQuery =
        normalizedQuery.length > 0 ? normalizedQuery : null;
      if (action.payload.length < 3) {
        state.status = "idle";
        state.carousels = [];
        state.resolvedCount = 0;
        state.errorMessage = "";
        state.currentRequestId = null;
        state.pageCursor = null;
        state.isLoadingMore = false;
        state.resolvedQuery = "";
        state.focusedCarouselIndex = 0;
        state.focusedTileIndices = [];
      }
    },

    /** Adds a term to the front of Recently Searched, deduped, capped at 6. */
    pushRecentSearch(state, action: PayloadAction<string>): void {
      addRecentSearchTerm(state, action.payload);
    },

    /**
     * Records the query the currently rendered results belong to, without
     * touching the focused region. Same admission rules as the
     * setFocusedField("results") transition — prefer the
     * `recordSearchTermToHistory()` thunk, which also persists the result.
     */
    recordResolvedQuery(state): void {
      if (state.status === "success" && state.resolvedCount > 0) {
        addRecentSearchTerm(state, state.resolvedQuery);
      }
    },

    /** Clears the Recently Searched list locally and resets its focus. */
    clearRecentSearchesLocal(state): void {
      state.recentSearches = [];
      state.focusedHistoryIndex = 0;
      state.historyRequestId = null;
    },

    /**
     * Sets the focused region. Transitioning into "results" from elsewhere
     * after a successful search is the point at which the current query is
     * recorded to Recently Searched — not when the search itself resolves —
     * so a query the user never navigates to never pollutes the history.
     *
     * Prefer `focusSearchResults()` over dispatching this with "results"
     * directly: that thunk performs the same transition and additionally
     * persists the updated list when — and only when — it actually changed.
     *
     * `resolvedQuery` (not the as-typed `query`) is what gets recorded: the
     * user may already have typed ahead while the previous query's results
     * are still the ones on screen, and the term they actually saw results
     * for is the one that belongs in the history.
     */
    setFocusedField(state, action: PayloadAction<SearchFocusedField>): void {
      const previousField: SearchFocusedField = state.focusedField;
      state.focusedField = action.payload;
      if (
        action.payload === "results" &&
        previousField !== "results" &&
        state.status === "success" &&
        state.resolvedCount > 0
      ) {
        addRecentSearchTerm(state, state.resolvedQuery);
      }
    },

    setFocusedHistoryIndex(state, action: PayloadAction<number>): void {
      state.focusedHistoryIndex = action.payload;
    },

    navigateHistoryUp(state): void {
      if (state.focusedHistoryIndex > 0) {
        state.focusedHistoryIndex -= 1;
      }
    },

    navigateHistoryDown(state): void {
      const maxIndex: number = state.recentSearches.length;
      if (state.focusedHistoryIndex < maxIndex) {
        state.focusedHistoryIndex += 1;
      }
    },

    setFocusedCarouselIndex(state, action: PayloadAction<number>): void {
      state.focusedCarouselIndex = action.payload;
    },

    setFocusedTileIndex(
      state,
      action: PayloadAction<{ carouselIndex: number; tileIndex: number }>
    ): void {
      const { carouselIndex, tileIndex } = action.payload;
      state.focusedTileIndices[carouselIndex] = tileIndex;
    },

    navigateResultsUp(state): void {
      if (state.focusedCarouselIndex > 0) {
        state.focusedCarouselIndex -= 1;
      }
    },

    navigateResultsDown(state): void {
      if (state.focusedCarouselIndex < state.carousels.length - 1) {
        state.focusedCarouselIndex += 1;
      }
    },

    /** Clears transient Search screen state when the screen unmounts. */
    resetSearchScreenState(state): void {
      state.query = "";
      state.pendingSearchQuery = null;
      state.status = "idle";
      state.errorMessage = "";
      state.carousels = [];
      state.resolvedCount = 0;
      state.currentRequestId = null;
      state.pageCursor = null;
      state.isLoadingMore = false;
      state.resolvedQuery = "";
      state.focusedField = "input";
      state.focusedHistoryIndex = 0;
      state.focusedCarouselIndex = 0;
      state.focusedTileIndices = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(runSmartSearch.pending, (state, action): void => {
        state.status = "loading";
        state.errorMessage = "";
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(runSmartSearch.fulfilled, (state, action): void => {
        if (
          action.meta.requestId !== state.currentRequestId ||
          (state.pendingSearchQuery !== null &&
            normalizeSearchQuery(action.meta.arg.query) !==
              state.pendingSearchQuery)
        ) {
          return;
        }
        state.carousels = action.payload.carousels;
        state.resolvedCount = action.payload.resolvedCount;
        state.pageCursor = action.payload.pageCursor;
        state.isLoadingMore = false;
        state.resolvedQuery = action.payload.resolvedQuery;
        state.status =
          action.payload.resolvedCount > 0 ? "success" : "noResults";
        state.pendingSearchQuery = null;
        state.focusedCarouselIndex = 0;
        state.focusedTileIndices = [];
      })
      .addCase(runSmartSearch.rejected, (state, action): void => {
        if (
          action.meta.requestId !== state.currentRequestId ||
          (state.pendingSearchQuery !== null &&
            normalizeSearchQuery(action.meta.arg.query) !==
              state.pendingSearchQuery)
        ) {
          return;
        }
        const payload = action.payload;
        if (payload !== undefined && payload.aborted === true) {
          return;
        }
        state.status = "error";
        state.pendingSearchQuery = null;
        state.errorMessage =
          payload !== undefined && payload.message !== undefined
            ? payload.message
            : UNKNOWN_ERROR_MESSAGE;
      })
      .addCase(loadMoreSmartSearchCarousels.pending, (state): void => {
        state.isLoadingMore = true;
      })
      .addCase(
        loadMoreSmartSearchCarousels.fulfilled,
        (state, action): void => {
          state.isLoadingMore = false;
          // Append only genuinely new rails (guard against a repeated cursor
          // returning carousels already present), then advance the cursor.
          const existingIds: Set<string> = new Set(
            state.carousels.map((rail: TCarouselRail): string => rail.id)
          );
          const freshRails: TCarouselRail[] = action.payload.carousels.filter(
            (rail: TCarouselRail): boolean => !existingIds.has(rail.id)
          );
          state.carousels.push(...freshRails);
          state.resolvedCount = state.carousels.reduce(
            (total: number, rail: TCarouselRail): number =>
              total + rail.items.length,
            0
          );
          state.pageCursor = action.payload.pageCursor;
        }
      )
      .addCase(loadMoreSmartSearchCarousels.rejected, (state): void => {
        state.isLoadingMore = false;
      })
      .addCase(fetchSearchHistoryThunk.pending, (state, action): void => {
        state.historyStatus = "loading";
        state.historyRequestId = action.meta.requestId;
      })
      .addCase(fetchSearchHistoryThunk.fulfilled, (state, action): void => {
        state.historyStatus = "idle";
        if (action.meta.requestId !== state.historyRequestId) {
          // Superseded by a locally recorded term or a Clear Recents that has
          // already been written through to the service — this response is
          // stale and must not resurrect/overwrite the newer list.
          return;
        }
        state.historyRequestId = null;
        state.recentSearches = action.payload.slice(
          0,
          SEARCH_MAX_RECENT_SEARCHES
        );
      })
      .addCase(fetchSearchHistoryThunk.rejected, (state, action): void => {
        state.historyStatus = "error";
        if (action.meta.requestId === state.historyRequestId) {
          state.historyRequestId = null;
        }
      })
      .addCase(deleteSearchHistoryThunk.pending, (state): void => {
        state.recentSearches = [];
        state.focusedHistoryIndex = 0;
        // A screen-entry fetch still in flight predates this clear; ignore
        // its response rather than let it resurrect the cleared list.
        state.historyRequestId = null;
        // RecentlySearched unmounts as soon as the list is empty, so focus
        // cannot stay on it (or on the Clear Recents button that was just
        // activated) — hand it back to the search input, which also restores
        // real DOM focus there via SearchInput's isFocused effect.
        state.focusedField = "input";
      })
      .addCase(deleteSearchHistoryThunk.rejected, (state): void => {
        // The local clear above is deliberately optimistic and is NOT rolled
        // back (PRD §2.4.6-7); surface the failure so callers can react.
        state.historyStatus = "error";
      });
  }
});

export const {
  setQuery,
  pushRecentSearch,
  recordResolvedQuery,
  clearRecentSearchesLocal,
  setFocusedField,
  setFocusedHistoryIndex,
  navigateHistoryUp,
  navigateHistoryDown,
  setFocusedCarouselIndex,
  setFocusedTileIndex,
  navigateResultsUp,
  navigateResultsDown,
  resetSearchScreenState
} = searchSlice.actions;

/** Order-sensitive equality check for two Recently Searched lists. */
const areTermListsEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length &&
  left.every((term: string, index: number): boolean => term === right[index]);

/**
 * Runs `mutate` and persists Recently Searched to TiVo Service if — and only
 * if — the list actually changed as a result.
 *
 * This guard is what keeps the API call to once per new search term: the
 * recording reducers only admit a term after a successful search, and
 * `addRecentSearchTerm` dedupes, so re-recording a term already at the front
 * of the list leaves the array untouched and issues no request.
 */
const persistIfHistoryChanged = (
  dispatch: UnityAppDispatch,
  getState: () => UnityRootState,
  mutate: () => void
): void => {
  const before: string[] = getState().search.recentSearches;
  mutate();
  const after: string[] = getState().search.recentSearches;
  if (!areTermListsEqual(before, after)) {
    dispatch(saveSearchHistoryThunk(after));
  }
};

/**
 * Moves focus into the results region, recording and persisting the query
 * behind the results the user is moving onto.
 *
 * Every path that moves the focused region onto a result goes through here —
 * 10-foot D-pad NAV_DOWN and the pointer scroll caret, cursor-mode mouse wheel
 * and tile focus — so both modes record and persist history identically.
 */
export const focusSearchResults =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    persistIfHistoryChanged(dispatch, getState, (): void => {
      dispatch(setFocusedField("results"));
    });
  };

/**
 * Records the query behind the on-screen results and persists it, WITHOUT
 * moving the focused region.
 *
 * For cursor mode, where hovering a tile is what visually focuses it (the
 * shared Carousel highlights the hovered tile) but must not disturb the
 * focused region — that drives the vertical rail scroll, and re-centering a
 * rail under the pointer would fight the user's mouse.
 */
export const recordSearchTermToHistory =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    persistIfHistoryChanged(dispatch, getState, (): void => {
      dispatch(recordResolvedQuery());
    });
  };

export const selectSearchQuery = (state: UnityRootState): string =>
  state.search.query;
export const selectSearchPendingQuery = (
  state: UnityRootState
): string | null => state.search.pendingSearchQuery;
export const selectSearchStatus = (state: UnityRootState) =>
  state.search.status;
export const selectSearchErrorMessage = (state: UnityRootState): string =>
  state.search.errorMessage;
export const selectSearchCarousels = (state: UnityRootState): TCarouselRail[] =>
  state.search.carousels;
export const selectSearchResolvedCount = (state: UnityRootState): number =>
  state.search.resolvedCount;
export const selectSearchPageCursor = (state: UnityRootState): string | null =>
  state.search.pageCursor;
export const selectSearchIsLoadingMore = (state: UnityRootState): boolean =>
  state.search.isLoadingMore;
export const selectSearchResolvedQuery = (state: UnityRootState): string =>
  state.search.resolvedQuery;
export const selectRecentSearches = (state: UnityRootState): string[] =>
  state.search.recentSearches;
export const selectSearchHistoryStatus = (
  state: UnityRootState
): SearchHistoryStatus => state.search.historyStatus;
export const selectSearchFocusedField = (
  state: UnityRootState
): SearchFocusedField => state.search.focusedField;
export const selectSearchFocusedHistoryIndex = (
  state: UnityRootState
): number => state.search.focusedHistoryIndex;
export const selectSearchFocusedCarouselIndex = (
  state: UnityRootState
): number => state.search.focusedCarouselIndex;
export const selectSearchFocusedTileIndices = (
  state: UnityRootState
): number[] => state.search.focusedTileIndices;

/**
 * Emits `contentHighlighted` for the tile at `carouselIndex`/`tileIndex` once
 * the dwell threshold has been met. No-op when the tile no longer resolves.
 */
export const trackSearchContentHighlighted = createAsyncThunk<
  void,
  { carouselIndex: number; tileIndex: number },
  { state: UnityRootState; rejectValue: { message: string } }
>(
  "search/trackSearchContentHighlighted",
  ({ carouselIndex, tileIndex }, { getState, rejectWithValue }) => {
    const fields = AnalyticsLogger.buildTileAnalyticsFields(
      selectSearchCarousels(getState()),
      carouselIndex,
      tileIndex
    );
    if (fields === null) {
      return;
    }
    try {
      SearchAnalyticsLogger.logContentHighlighted(fields);
    } catch (error: unknown) {
      Log.e(TAG, "trackSearchContentHighlighted()", error);
      return rejectWithValue({ message: UNKNOWN_ERROR_MESSAGE });
    }
  }
);

/**
 * Emits `contentSelected` for the tile at `carouselIndex`/`tileIndex`. No-op
 * when the tile no longer resolves.
 */
export const trackSearchContentSelected = createAsyncThunk<
  void,
  { carouselIndex: number; tileIndex: number },
  { state: UnityRootState; rejectValue: { message: string } }
>(
  "search/trackSearchContentSelected",
  ({ carouselIndex, tileIndex }, { getState, rejectWithValue }) => {
    const state = getState();
    const fields = AnalyticsLogger.buildTileAnalyticsFields(
      selectSearchCarousels(state),
      carouselIndex,
      tileIndex
    );
    if (fields === null) {
      return;
    }
    try {
      SearchAnalyticsLogger.logContentSelected({
        ...fields,
        previous_screen_id: selectPreviousUrl(state)
      });
    } catch (error: unknown) {
      Log.e(TAG, "trackSearchContentSelected()", error);
      return rejectWithValue({ message: UNKNOWN_ERROR_MESSAGE });
    }
  }
);

export default searchSlice.reducer;
